import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useMatchRoute, useNavigate, useParams, useRouterState } from "@tanstack/react-router";
import {
  Check,
  ChevronsUpDown,
  Clapperboard,
  Command as CommandIcon,
  Keyboard,
  LayoutTemplate,
  LogOut,
  Monitor,
  Moon,
  Newspaper as NewspaperIcon,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Settings,
  Sun,
  Trash2,
  UserRound,
  UserRoundCog,
  Users,
  Film,
} from "lucide-react";
import * as React from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { getSession, logout, setSession } from "@/api/auth";
import { api, unwrap } from "@/api/client";
import { connectLive, useLive } from "@/api/live";
import { newspapersQuery } from "@/api/queries";
import { LANGUAGE_NAMES } from "@/api/types";
import { Avatar } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, Tip } from "@/components/ui/overlay";
import { cn } from "@/lib/cn";
import { lastNewspaper, rememberNewspaper, ROLE_LABEL, useCanManage, useMe, useNewspaperRole } from "@/lib/session";
import { applyTheme, getTheme, type Theme } from "@/lib/theme";
import { CommandPalette } from "./CommandPalette";
import { ShortcutsDialog } from "./ShortcutsDialog";

/** The newspaper the current page is about: from the URL, else the last one used. */
export function useCurrentNewspaperId(): string | null {
  const params = useParams({ strict: false }) as { npId?: string };
  const { data } = useQuery(newspapersQuery);
  const fromUrl = params.npId;
  React.useEffect(() => {
    if (fromUrl) rememberNewspaper(fromUrl);
  }, [fromUrl]);
  if (fromUrl) return fromUrl;
  const last = lastNewspaper();
  if (last && data?.some((n) => n.id === last)) return last;
  return data?.[0]?.id ?? null;
}

function NavItem({
  to,
  params,
  icon,
  label,
  collapsed,
  count,
}: {
  to: string;
  params?: Record<string, string>;
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
  count?: number;
}) {
  const link = (
    <Link
      to={to}
      params={params as never}
      className="group flex h-8 items-center gap-2.5 rounded-md px-2 text-sm text-muted transition-colors hover:bg-hover hover:text-fg [&.active]:bg-active [&.active]:text-fg [&_svg]:size-4"
      activeOptions={{ exact: false, includeSearch: false }}
    >
      {icon}
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
      {!collapsed && count != null && count > 0 && <span className="tnum text-xs text-subtle">{count}</span>}
    </Link>
  );
  return collapsed ? (
    <Tip content={label} side="right">
      {link}
    </Tip>
  ) : (
    link
  );
}

function NewspaperSwitcher({ collapsed }: { collapsed: boolean }) {
  const { data = [] } = useQuery(newspapersQuery);
  const current = useCurrentNewspaperId();
  const np = data.find((n) => n.id === current);
  const navigate = useNavigate();
  const me = useMe();
  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          className={cn(
            "flex h-10 w-full items-center gap-2 rounded-md px-2 text-left transition-colors hover:bg-hover",
            collapsed && "justify-center px-0",
          )}
          aria-label="Switch newspaper"
        >
          <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-surface">
            {np?.logo_url ? (
              <img src={np.logo_url} alt="" className="size-full object-contain p-0.5" />
            ) : (
              <NewspaperIcon className="size-3.5 text-subtle" />
            )}
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{np?.name ?? "No newspaper"}</span>
                {np && <span className="block truncate text-2xs text-subtle">{LANGUAGE_NAMES[np.language]} · {np.handle}</span>}
              </span>
              <ChevronsUpDown className="size-3.5 text-subtle" />
            </>
          )}
        </button>
      </MenuTrigger>
      <MenuContent align="start" className="w-64">
        <MenuLabel>Newspapers</MenuLabel>
        {data.map((n) => (
          <MenuItem
            key={n.id}
            onSelect={() => navigate({ to: "/n/$npId/reels", params: { npId: n.id } })}
            icon={<NewspaperIcon />}
          >
            <span className="flex items-center justify-between gap-2">
              <span className="truncate">{n.name}</span>
              {n.id === current && <Check className="size-3.5 text-accent-text" />}
            </span>
          </MenuItem>
        ))}
        {data.length === 0 && <div className="px-2 py-1.5 text-sm text-muted">You're not on any newspaper yet.</div>}
        {me.role === "admin" && (
          <>
            <MenuSeparator />
            <MenuItem icon={<Plus />} onSelect={() => navigate({ to: "/admin/newspapers", search: { new: true } })}>
              Add a newspaper
            </MenuItem>
          </>
        )}
      </MenuContent>
    </Menu>
  );
}

function UserMenu({ collapsed, onShortcuts }: { collapsed: boolean; onShortcuts: () => void }) {
  const me = useMe();
  const myRole = useNewspaperRole(useCurrentNewspaperId());
  const [theme, setTheme] = React.useState<Theme>(getTheme());
  const navigate = useNavigate();
  const pick = (t: Theme) => {
    applyTheme(t);
    setTheme(t);
  };
  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          className={cn("flex h-9 w-full items-center gap-2 rounded-md px-2 text-left hover:bg-hover", collapsed && "justify-center px-0")}
          aria-label="Account menu"
        >
          <Avatar name={me.display_name || me.email} />
          {!collapsed && (
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm">{me.display_name || me.email}</span>
              <span className="block truncate text-2xs text-subtle">{myRole ? ROLE_LABEL[myRole] : "Member"}</span>
            </span>
          )}
        </button>
      </MenuTrigger>
      <MenuContent align="start" side="top" className="w-56">
        <MenuLabel>{me.email}</MenuLabel>
        <MenuItem icon={<UserRound />} onSelect={() => navigate({ to: "/account" })}>
          Account
        </MenuItem>
        <MenuItem icon={<Keyboard />} shortcut="?" onSelect={onShortcuts}>
          Keyboard shortcuts
        </MenuItem>
        <MenuSeparator />
        <MenuLabel>Theme</MenuLabel>
        {(
          [
            ["light", "Light", <Sun key="l" />],
            ["dark", "Dark", <Moon key="d" />],
            ["system", "System", <Monitor key="s" />],
          ] as const
        ).map(([v, label, icon]) => (
          <MenuItem key={v} icon={icon} onSelect={(e) => (e.preventDefault(), pick(v))}>
            <span className="flex items-center justify-between">
              {label}
              {theme === v && <Check className="size-3.5 text-accent-text" />}
            </span>
          </MenuItem>
        ))}
        <MenuSeparator />
        <MenuItem icon={<LogOut />} onSelect={() => logout().then(() => navigate({ to: "/login", search: {} }))}>
          Sign out
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

export function Shell() {
  const me = useMe();
  const qc = useQueryClient();
  const np = useCurrentNewspaperId();
  const canManage = useCanManage(np);
  const matchRoute = useMatchRoute();
  const inReel = !!matchRoute({ to: "/reels/$reelId", fuzzy: true });
  const [pinnedCollapsed, setPinnedCollapsed] = React.useState(() => localStorage.getItem("rs.sidebar") === "1");
  const collapsed = pinnedCollapsed || inReel;
  const [palette, setPalette] = React.useState(false);
  const [shortcuts, setShortcuts] = React.useState(false);
  const connected = useLive((s) => s.connected);
  const path = useRouterState({ select: (s) => s.location.pathname });

  React.useEffect(() => connectLive(qc), [qc]);
  const meQ = useQuery({ queryKey: ["me"], queryFn: () => unwrap(api.GET("/api/me")), refetchInterval: 60_000 });
  React.useEffect(() => {
    const s = getSession();
    if (meQ.data && s && JSON.stringify(meQ.data) !== JSON.stringify(s.user)) setSession({ ...s, user: meQ.data });
  }, [meQ.data]);
  useHotkeys("mod+k", (e) => (e.preventDefault(), setPalette((o) => !o)), { enableOnFormTags: true });
  useHotkeys("shift+slash", () => setShortcuts((o) => !o));
  useHotkeys("bracketleft", () => {
    setPinnedCollapsed((c) => {
      localStorage.setItem("rs.sidebar", c ? "0" : "1");
      return !c;
    });
  });

  return (
    <div className="flex h-full">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2">
        Skip to content
      </a>
      <aside
        className={cn(
          "flex shrink-0 flex-col border-r border-border bg-sidebar transition-[width] duration-150",
          collapsed ? "w-14" : "w-60",
        )}
      >
        <div className={cn("flex h-12 items-center gap-2 px-3", collapsed && "justify-center px-0")}>
          <Link to="/" className="flex items-center gap-2 font-semibold" aria-label="Reel Studio home">
            <span className="flex size-6 items-center justify-center rounded-md bg-primary text-primary-fg">
              <Clapperboard className="size-3.5" />
            </span>
            {!collapsed && <span className="text-sm">Reel Studio</span>}
          </Link>
          {!collapsed && (
            <Tip content={<>Collapse sidebar <span className="opacity-60">[</span></>}>
              <Button
                variant="ghost"
                size="icon-sm"
                className="ml-auto"
                aria-label="Collapse sidebar"
                onClick={() => {
                  localStorage.setItem("rs.sidebar", "1");
                  setPinnedCollapsed(true);
                }}
              >
                <PanelLeftClose />
              </Button>
            </Tip>
          )}
        </div>
        <div className="px-2">
          <NewspaperSwitcher collapsed={collapsed} />
        </div>
        <nav className="mt-2 flex flex-1 flex-col gap-0.5 overflow-y-auto px-2" aria-label="Main">
          {np && (
            <>
              <NavItem to="/n/$npId/reels" params={{ npId: np }} icon={<Film />} label="Reels" collapsed={collapsed} />
              <NavItem to="/n/$npId/trash" params={{ npId: np }} icon={<Trash2 />} label="Trash" collapsed={collapsed} />
              {canManage && (
                <>
                  {!collapsed && <div className="mt-4 px-2 pb-1 text-2xs font-medium uppercase tracking-wide text-subtle">Newspaper</div>}
                  <NavItem to="/n/$npId/templates" params={{ npId: np }} icon={<LayoutTemplate />} label="Templates" collapsed={collapsed} />
                  <NavItem to="/n/$npId/people" params={{ npId: np }} icon={<UserRoundCog />} label="People" collapsed={collapsed} />
                  <NavItem to="/n/$npId/settings/$tab" params={{ npId: np, tab: "brand" }} icon={<Settings />} label="Settings" collapsed={collapsed} />
                </>
              )}
            </>
          )}
          {me.role === "admin" && (
            <div className="mt-4 flex flex-col gap-0.5">
              {!collapsed && <div className="px-2 pb-1 text-2xs font-medium uppercase tracking-wide text-subtle">Super admin</div>}
              <NavItem to="/admin/members" icon={<Users />} label="Everyone & invites" collapsed={collapsed} />
              <NavItem to="/admin/newspapers" icon={<NewspaperIcon />} label="Newspapers" collapsed={collapsed} />
            </div>
          )}
        </nav>
        <div className="flex flex-col gap-1 border-t border-border p-2">
          {collapsed ? (
            !inReel && (
              <Tip content="Expand sidebar" side="right">
                <Button
                  variant="ghost"
                  size="icon"
                  className="mx-auto"
                  aria-label="Expand sidebar"
                  onClick={() => {
                    localStorage.setItem("rs.sidebar", "0");
                    setPinnedCollapsed(false);
                  }}
                >
                  <PanelLeftOpen />
                </Button>
              </Tip>
            )
          ) : (
            <button
              onClick={() => setPalette(true)}
              className="flex h-8 items-center gap-2 rounded-md px-2 text-sm text-muted hover:bg-hover hover:text-fg"
            >
              <CommandIcon className="size-4" />
              <span className="flex-1 text-left">Go to…</span>
              <span className="text-2xs text-subtle">⌘K</span>
            </button>
          )}
          <UserMenu collapsed={collapsed} onShortcuts={() => setShortcuts(true)} />
          {!connected && !collapsed && (
            <div className="px-2 text-2xs text-subtle" title="Live updates are reconnecting; the page refreshes every few seconds meanwhile.">
              ● Reconnecting…
            </div>
          )}
        </div>
      </aside>
      <main id="main" className="flex min-w-0 flex-1 flex-col overflow-hidden" key={path.startsWith("/reels/") ? "reel" : "page"}>
        <Outlet />
      </main>
      <CommandPalette open={palette} onOpenChange={setPalette} onShortcuts={() => setShortcuts(true)} />
      <ShortcutsDialog open={shortcuts} onOpenChange={setShortcuts} />
    </div>
  );
}
